import { type Player, PlayerStatus } from '@infiltre/shared';
import { type MessageKey, useT } from '@/shared/lib/i18n';
import { roleTint } from '@/shared/lib/role';
import { AdaptiveDialog, BrandMark, Button, HandKicker, PhaseTag, Pin } from '@/shared/ui';
import type { EliminationEntry } from '../model/slice';

export type RoundRecapMode = 'between' | 'end';

export type RoundRecapProps = {
  open: boolean;
  onClose: () => void;
  history: readonly EliminationEntry[];
  players: readonly Player[];
  mode: RoundRecapMode;
};

/** Case-log recap sheet — `between` (lookback) or `end` (game-over dossier). */
export const RoundRecap = ({ open, onClose, history, players, mode }: RoundRecapProps) => {
  const t = useT();

  const aliveCount = players.filter((p) => p.status === PlayerStatus.Alive).length;

  return (
    <AdaptiveDialog
      open={open}
      onClose={onClose}
      ariaLabel={t(mode === 'between' ? 'recap.title_between' : 'recap.title_end')}
      closeLabel={t('recap.close')}
      surface="bare"
      hideHandle
    >
      <div className="paper-dossier relative flex flex-col gap-5 px-7 py-6 text-ink">
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />

        <header className="flex flex-col gap-1">
          <PhaseTag size={11} color="rgba(0,0,0,.5)">
            {t(mode === 'between' ? 'recap.eyebrow_between' : 'recap.eyebrow_end')}
          </PhaseTag>
          <BrandMark size={'clamp(36px, 4vw, 46px)'} className="mt-1">
            {t(mode === 'between' ? 'recap.title_between' : 'recap.title_end')}
          </BrandMark>
          <HandKicker size={20} color="var(--color-pencil)" rot={0} className="mt-2">
            — {t(mode === 'between' ? 'recap.sub_between' : 'recap.sub_end')}
          </HandKicker>
        </header>

        {history.length === 0 ? (
          <Empty label={t('recap.empty')} />
        ) : (
          <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto pr-1">
            {history.map((entry, i) => (
              <RecapRow key={`${entry.round}-${entry.playerId}`} entry={entry} index={i} />
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-ink/85 border-b border-t py-2.5">
          <span className="font-type text-[13px] text-ink tracking-[0.04em]">
            {t('recap.still_in_room')}
          </span>
          <span className="font-display font-extrabold text-[16px] text-ink tabular-nums">
            {aliveCount}/{players.length}
          </span>
        </div>

        <footer className="flex justify-end pt-1">
          <Button variant="orange" fullWidth={false} onClick={onClose}>
            {t('recap.close')} →
          </Button>
        </footer>
      </div>
    </AdaptiveDialog>
  );
};

const RecapRow = ({ entry, index }: { entry: EliminationEntry; index: number }) => {
  const t = useT();
  const tilt = index % 2 === 0 ? -0.4 : 0.6;
  const tint = roleTint(entry.role);
  return (
    <div
      className="flex items-center gap-3 border-ink/85 border-b py-2"
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      <div className="flex h-9 w-9 shrink-0 flex-col items-center justify-center border-[1.5px] border-ink bg-ink font-display text-cream">
        <span className="font-extrabold text-[7px] uppercase tracking-[0.18em] opacity-70">
          {t('recap.round_short')}
        </span>
        <span className="font-extrabold text-[14px] leading-none">{entry.round}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="truncate font-display font-extrabold text-[14px] text-ink line-through"
          style={{
            textDecorationColor: 'color-mix(in srgb, var(--color-ink) 60%, transparent)',
            textDecorationThickness: '1.5px',
          }}
        >
          {entry.name}
        </div>
        <div className="mt-0.5 font-mono text-[10px] text-ink/55 tracking-[0.1em]">
          {t('recap.eliminated_meta', { count: String(entry.aliveAfter) })}
        </div>
      </div>
      <div
        className="shrink-0 px-2 py-1 font-display font-extrabold text-[9px] uppercase tracking-[0.16em]"
        style={{
          border: `1.5px solid ${tint}`,
          color: tint,
          background: 'color-mix(in srgb, white 60%, transparent)',
          transform: 'rotate(4deg)',
        }}
      >
        {t(`role.${entry.role}` as MessageKey)}
      </div>
    </div>
  );
};

const Empty = ({ label }: { label: string }) => (
  <div className="border-ink/30 border-dashed border-y px-4 py-6 text-center font-type text-[13px] text-ink/55">
    {label}
  </div>
);
